import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { profileAPI } from '../services/api';
import { useAuth } from '../context/AuthContext';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import { VerificationBadge, RoleBadge } from '../components/Badges';
import FraudShieldPanel from '../components/FraudShieldPanel';
import ReportIssueDialog from '../components/ReportIssueDialog';
import FMCSAVerificationCard from '../components/FMCSAVerificationCard';
import {
  User, Building, Phone, Mail, MapPin, Truck, FileText,
  Calendar, Edit, Save, X, Loader2, ArrowLeft, ShieldCheck, Flag
} from 'lucide-react';
import { toast } from 'sonner';

const ProfilePage = () => {
  const { userId } = useParams();
  const navigate = useNavigate();
  const { user, profile: myProfile, updateProfile, isAuthenticated } = useAuth();
  const [profile, setProfile] = useState(null);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(false);
  const [saving, setSaving] = useState(false);
  const [editData, setEditData] = useState({});
  const [reportDialogOpen, setReportDialogOpen] = useState(false);

  const isOwnProfile = !userId || userId === user?.id;

  useEffect(() => {
    loadProfile();
  }, [userId]);

  const loadProfile = async () => {
    try {
      if (isOwnProfile && myProfile) {
        setProfile(myProfile);
        setEditData(myProfile);
      } else {
        const response = await profileAPI.getProfile(userId || user?.id);
        setProfile(response.data);
        setEditData(response.data);
      }
    } catch (error) {
      toast.error('Failed to load profile');
      navigate('/dashboard');
    } finally {
      setLoading(false);
    }
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      const response = await profileAPI.updateProfile({
        full_name: editData.full_name,
        company_name: editData.company_name,
        phone: editData.phone,
        mc_number: editData.mc_number,
        dot_number: editData.dot_number,
        service_type: editData.service_type,
        equipment_type: editData.equipment_type,
        region: editData.region,
        bio: editData.bio,
      });
      setProfile(response.data);
      updateProfile(response.data);
      setEditing(false);
      toast.success('Profile updated!');
    } catch (error) {
      toast.error('Failed to update profile');
    } finally {
      setSaving(false);
    }
  };

  const serviceTypes = [
    { value: 'auto_transport', label: 'Auto Transport' },
    { value: 'hotshot', label: 'Hotshot' },
    { value: 'freight', label: 'Freight' },
    { value: 'multi_service', label: 'Multi-Service' },
  ];

  const equipmentTypes = [
    { value: 'open_carrier', label: 'Open Carrier' },
    { value: 'enclosed_carrier', label: 'Enclosed Carrier' },
    { value: 'hotshot', label: 'Hotshot Trailer' },
    { value: 'flatbed', label: 'Flatbed' },
    { value: 'dry_van', label: 'Dry Van' },
    { value: 'reefer', label: 'Reefer' },
  ];

  if (loading) {
    return (
      <div className="min-h-screen bg-background pt-20 flex items-center justify-center">
        <Loader2 className="w-8 h-8 animate-spin text-primary" />
      </div>
    );
  }

  if (!profile) return null;

  return (
    <div className="min-h-screen bg-background pt-20 pb-12 px-4">
      <div className="max-w-3xl mx-auto">
        {!isOwnProfile && (
          <Button
            variant="ghost"
            onClick={() => navigate(-1)}
            className="mb-6"
            data-testid="back-btn"
          >
            <ArrowLeft className="w-4 h-4 mr-2" />
            Back
          </Button>
        )}

        {/* Profile Header */}
        <Card className="border-white/5 bg-card/50 mb-6 animate-fade-in" data-testid="profile-header-card">
          <CardContent className="pt-6">
            <div className="flex flex-col md:flex-row items-start md:items-center gap-6">
              <div className="w-24 h-24 rounded-full bg-secondary flex items-center justify-center">
                <User className="w-12 h-12 text-muted-foreground" />
              </div>
              <div className="flex-1">
                <div className="flex items-center gap-3 mb-2">
                  <h1 className="text-3xl font-bold font-['Barlow_Condensed'] uppercase tracking-tight">
                    {profile.full_name}
                  </h1>
                  <VerificationBadge status={profile.verification_status || 'pending'} />
                </div>
                <div className="flex flex-wrap items-center gap-3">
                  {profile.role && <RoleBadge role={profile.role} />}
                  {profile.company_name && (
                    <span className="text-muted-foreground flex items-center gap-1">
                      <Building className="w-4 h-4" />
                      {profile.company_name}
                    </span>
                  )}
                </div>
              </div>
              {isOwnProfile && !editing && (
                <Button
                  variant="outline"
                  onClick={() => setEditing(true)}
                  data-testid="edit-profile-btn"
                >
                  <Edit className="w-4 h-4 mr-2" />
                  Edit Profile
                </Button>
              )}
            </div>
          </CardContent>
        </Card>

        {/* Profile Details */}
        <Card className="border-white/5 bg-card/50 animate-fade-in delay-100" data-testid="profile-details-card">
          <CardHeader>
            <CardTitle className="font-['Barlow_Condensed'] uppercase tracking-wide">
              Profile Details
            </CardTitle>
            {isOwnProfile && (
              <CardDescription>
                Your profile information is visible to other users
              </CardDescription>
            )}
          </CardHeader>
          <CardContent>
            {editing ? (
              <div className="space-y-6">
                <div className="grid md:grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label htmlFor="full_name">Full Name</Label>
                    <Input
                      id="full_name"
                      value={editData.full_name || ''}
                      onChange={(e) => setEditData({ ...editData, full_name: e.target.value })}
                      className="bg-secondary/30 border-white/10"
                      data-testid="edit-fullname-input"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="company_name">Company Name</Label>
                    <Input
                      id="company_name"
                      value={editData.company_name || ''}
                      onChange={(e) => setEditData({ ...editData, company_name: e.target.value })}
                      className="bg-secondary/30 border-white/10"
                      data-testid="edit-company-input"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="phone">Phone</Label>
                    <Input
                      id="phone"
                      value={editData.phone || ''}
                      onChange={(e) => setEditData({ ...editData, phone: e.target.value })}
                      className="bg-secondary/30 border-white/10"
                      data-testid="edit-phone-input"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="region">Region</Label>
                    <Input
                      id="region"
                      value={editData.region || ''}
                      onChange={(e) => setEditData({ ...editData, region: e.target.value })}
                      className="bg-secondary/30 border-white/10"
                      data-testid="edit-region-input"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="mc_number">MC Number</Label>
                    <Input
                      id="mc_number"
                      value={editData.mc_number || ''}
                      onChange={(e) => setEditData({ ...editData, mc_number: e.target.value })}
                      className="bg-secondary/30 border-white/10"
                      data-testid="edit-mc-input"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="dot_number">DOT Number</Label>
                    <Input
                      id="dot_number"
                      value={editData.dot_number || ''}
                      onChange={(e) => setEditData({ ...editData, dot_number: e.target.value })}
                      className="bg-secondary/30 border-white/10"
                      data-testid="edit-dot-input"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label>Service Type</Label>
                    <Select
                      value={editData.service_type || ''}
                      onValueChange={(value) => setEditData({ ...editData, service_type: value })}
                    >
                      <SelectTrigger className="bg-secondary/30 border-white/10" data-testid="edit-service-type">
                        <SelectValue placeholder="Select service type" />
                      </SelectTrigger>
                      <SelectContent>
                        {serviceTypes.map((type) => (
                          <SelectItem key={type.value} value={type.value}>
                            {type.label}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                  <div className="space-y-2">
                    <Label>Equipment Type</Label>
                    <Select
                      value={editData.equipment_type || ''}
                      onValueChange={(value) => setEditData({ ...editData, equipment_type: value })}
                    >
                      <SelectTrigger className="bg-secondary/30 border-white/10" data-testid="edit-equipment-type">
                        <SelectValue placeholder="Select equipment type" />
                      </SelectTrigger>
                      <SelectContent>
                        {equipmentTypes.map((type) => (
                          <SelectItem key={type.value} value={type.value}>
                            {type.label}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                </div>
                <div className="space-y-2">
                  <Label htmlFor="bio">Bio</Label>
                  <Textarea
                    id="bio"
                    placeholder="Tell others about yourself or your company..."
                    value={editData.bio || ''}
                    onChange={(e) => setEditData({ ...editData, bio: e.target.value })}
                    rows={4}
                    className="bg-secondary/30 border-white/10"
                    data-testid="edit-bio-input"
                  />
                </div>
                <div className="flex gap-3">
                  <Button
                    variant="outline"
                    onClick={() => {
                      setEditing(false);
                      setEditData(profile);
                    }}
                  >
                    <X className="w-4 h-4 mr-2" />
                    Cancel
                  </Button>
                  <Button
                    onClick={handleSave}
                    disabled={saving}
                    className="glow-gold"
                    data-testid="save-profile-btn"
                  >
                    {saving ? (
                      <>
                        <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                        Saving...
                      </>
                    ) : (
                      <>
                        <Save className="w-4 h-4 mr-2" />
                        Save Changes
                      </>
                    )}
                  </Button>
                </div>
              </div>
            ) : (
              <div className="space-y-6">
                <div className="grid md:grid-cols-2 gap-6">
                  {profile.email && (
                    <div className="flex items-center gap-3">
                      <Mail className="w-5 h-5 text-muted-foreground" />
                      <div>
                        <p className="text-xs text-muted-foreground">Email</p>
                        <p className="font-medium">{profile.email}</p>
                      </div>
                    </div>
                  )}
                  {profile.phone && (
                    <div className="flex items-center gap-3">
                      <Phone className="w-5 h-5 text-muted-foreground" />
                      <div>
                        <p className="text-xs text-muted-foreground">Phone</p>
                        <p className="font-medium">{profile.phone}</p>
                      </div>
                    </div>
                  )}
                  {profile.region && (
                    <div className="flex items-center gap-3">
                      <MapPin className="w-5 h-5 text-muted-foreground" />
                      <div>
                        <p className="text-xs text-muted-foreground">Region</p>
                        <p className="font-medium">{profile.region}</p>
                      </div>
                    </div>
                  )}
                  {profile.service_type && (
                    <div className="flex items-center gap-3">
                      <Truck className="w-5 h-5 text-muted-foreground" />
                      <div>
                        <p className="text-xs text-muted-foreground">Service Type</p>
                        <p className="font-medium capitalize">{profile.service_type?.replace('_', ' ')}</p>
                      </div>
                    </div>
                  )}
                  {profile.equipment_type && (
                    <div className="flex items-center gap-3">
                      <Truck className="w-5 h-5 text-muted-foreground" />
                      <div>
                        <p className="text-xs text-muted-foreground">Equipment Type</p>
                        <p className="font-medium capitalize">{profile.equipment_type?.replace('_', ' ')}</p>
                      </div>
                    </div>
                  )}
                  {profile.mc_number && (
                    <div className="flex items-center gap-3">
                      <FileText className="w-5 h-5 text-muted-foreground" />
                      <div>
                        <p className="text-xs text-muted-foreground">MC Number</p>
                        <p className="font-medium">{profile.mc_number}</p>
                      </div>
                    </div>
                  )}
                  {profile.dot_number && (
                    <div className="flex items-center gap-3">
                      <FileText className="w-5 h-5 text-muted-foreground" />
                      <div>
                        <p className="text-xs text-muted-foreground">DOT Number</p>
                        <p className="font-medium">{profile.dot_number}</p>
                      </div>
                    </div>
                  )}
                  {profile.created_at && (
                    <div className="flex items-center gap-3">
                      <Calendar className="w-5 h-5 text-muted-foreground" />
                      <div>
                        <p className="text-xs text-muted-foreground">Member Since</p>
                        <p className="font-medium">
                          {new Date(profile.created_at).toLocaleDateString('en-US', {
                            month: 'long',
                            year: 'numeric',
                          })}
                        </p>
                      </div>
                    </div>
                  )}
                </div>
                {profile.bio && (
                  <div className="pt-4 border-t border-border">
                    <p className="text-xs text-muted-foreground mb-2">About</p>
                    <p className="text-foreground">{profile.bio}</p>
                  </div>
                )}
              </div>
            )}
          </CardContent>
        </Card>

        {/* Verification Status */}
        {isOwnProfile && (
          <Card className="border-white/5 bg-card/50 mt-6 animate-fade-in delay-200" data-testid="verification-status-card">
            <CardHeader>
              <CardTitle className="font-['Barlow_Condensed'] uppercase tracking-wide flex items-center gap-2">
                <ShieldCheck className="w-5 h-5 text-primary" />
                Verification Status
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-muted-foreground text-sm mb-2">
                    Complete verification to unlock all features and build trust
                  </p>
                  <VerificationBadge status={profile.verification_status || 'pending'} size="lg" />
                </div>
                {profile.verification_status !== 'verified' && (
                  <Button
                    variant="outline"
                    onClick={() => navigate('/documents')}
                    data-testid="upload-documents-btn"
                  >
                    Upload Documents
                  </Button>
                )}
              </div>
            </CardContent>
          </Card>
        )}

        {/* Security Settings */}
        {isOwnProfile && (
          <Card className="border-white/5 bg-card/50 mt-6 animate-fade-in delay-300" data-testid="security-settings-card">
            <CardHeader>
              <CardTitle className="font-['Barlow_Condensed'] uppercase tracking-wide flex items-center gap-2">
                <ShieldCheck className="w-5 h-5 text-primary" />
                Security
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-muted-foreground text-sm">
                    Manage two-factor authentication and active sessions
                  </p>
                </div>
                <Button
                  variant="outline"
                  onClick={() => navigate('/security')}
                  data-testid="security-settings-btn"
                >
                  Security Settings
                </Button>
              </div>
            </CardContent>
          </Card>
        )}

        {/* FMCSA Verification Panel - Show for carriers/brokers */}
        {(profile.role === 'carrier' || profile.role === 'broker' || profile.dot_number) && (
          <div className="mt-6 animate-fade-in delay-350">
            <FMCSAVerificationCard 
              dotNumber={profile.dot_number}
              isOwnProfile={isOwnProfile}
              userId={profile.user_id}
            />
          </div>
        )}

        {/* Fraud Shield Panel */}
        <div className="mt-6 animate-fade-in delay-400">
          <FraudShieldPanel 
            userId={profile.user_id} 
            isOwnProfile={isOwnProfile} 
          />
        </div>

        {/* Report Issue Button - Only show for other users */}
        {!isOwnProfile && isAuthenticated && (
          <div className="mt-6 animate-fade-in delay-500">
            <Button
              variant="outline"
              className="w-full border-red-500/30 text-red-400 hover:bg-red-500/10"
              onClick={() => setReportDialogOpen(true)}
              data-testid="report-user-btn"
            >
              <Flag className="w-4 h-4 mr-2" />
              Report Issue with This User
            </Button>
          </div>
        )}
      </div>

      {/* Report Issue Dialog */}
      <ReportIssueDialog
        open={reportDialogOpen}
        onOpenChange={setReportDialogOpen}
        relatedUserId={profile?.user_id}
        relatedUserName={profile?.full_name}
        contextType="user"
      />
    </div>
  );
};

export default ProfilePage;
